import styled, { keyframes } from 'styled-components';
import { Button, Input, Table, Tag, Modal, Drawer, Select } from 'antd';
import { COLORS } from '../../../constants/colors';

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideInFromTop = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const PageContainer = styled.div`
  min-height: 100vh;
  
  .header-animate-in {
    animation: ${slideInFromTop} 0.6s ease-out forwards;
    opacity: 0;
  }

  .table-animate-in {
    animation: ${fadeInScale} 0.6s ease-out forwards;
    animation-delay: 0.2s;
    opacity: 0;
  }
`;

export const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

export const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  flex: 1;
`;

export const SearchInput = styled(Input)`
  max-width: 320px;
  height: 40px !important;
  min-height: 40px !important;
  border-radius: 8px !important;
  border: 1px solid ${COLORS.border} !important;
  font-size: 14px !important;
  box-shadow: none !important;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${COLORS.$primaryBlue} !important;
  }

  &:focus,
  &.ant-input-affix-wrapper-focused {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }

  .ant-input {
    font-size: 14px;
  }

  .ant-input-prefix {
    margin-right: 8px;

    .anticon {
      color: ${COLORS.textMuted};
      font-size: 16px;
    }
  }
`;

export const FilterButton = styled(Button)`
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  min-width: 100px !important;
  border-radius: 8px !important;
  border: 1px solid ${COLORS.border} !important;
  background: white !important;
  color: ${COLORS.textDark} !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: none !important;
  transition: all 0.2s ease;
  font-size: 14px !important;
  font-weight: 500 !important;
  gap: 8px !important;

  &:hover {
    border-color: ${COLORS.$primaryBlue} !important;
    color: ${COLORS.$primaryBlue} !important;
    background: white !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1) !important;
  }

  &:focus {
    border-color: ${COLORS.$primaryBlue} !important;
    color: ${COLORS.$primaryBlue} !important;
    background: white !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }

  .anticon {
    font-size: 16px;
  }

  .filter-count {
    background: ${COLORS.$primaryBlue};
    color: white;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    margin-left: 4px;
  }
`;

export const StyledFilterDrawer = styled(Drawer)`
  .ant-drawer-header {
    border-bottom: 1px solid ${COLORS.border};
    padding: 16px 24px;

    .ant-drawer-title {
      font-weight: 600;
      color: ${COLORS.textDark};
      font-size: 16px;
    }
  }

  .ant-drawer-body {
    padding: 24px;
    background: #fafafa;
  }

  .ant-drawer-close {
    color: ${COLORS.textMedium};

    &:hover {
      color: ${COLORS.textDark};
    }
  }
`;

export const DrawerSelect = styled(Select)`
  width: 100% !important;
  height: 40px !important;

  .ant-select-selector {
    height: 40px !important;
    border-radius: 8px !important;
    border: 1px solid ${COLORS.border} !important;
    padding: 4px 12px !important;
    box-shadow: none !important;
    transition: all 0.2s ease;

    &:hover {
      border-color: ${COLORS.$primaryBlue} !important;
    }
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }

  .ant-select-selection-item {
    line-height: 32px !important;
    font-size: 14px;
  }
`;

export const ActionButton = styled(Button)`
  &&& {
    height: 34px;
    min-height: 34px !important;
    max-height: 34px !important;
    padding: 0 16px;
    border-radius: 8px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:active {
      transform: translateY(0);
    }
  }
`;

export const StyledTable = styled(Table)`
  background: white;
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .ant-table {
    border-radius: 0;
    overflow: hidden;
    background: white;
    border: none;
  }

  .ant-table-thead > tr > th {
    background: white !important;
    color: ${COLORS.textDark} !important;
    border-bottom: 3px solid ${COLORS.$charcoalBlack} !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    height: 52px !important;
    padding: 14px 20px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    white-space: nowrap !important;
    position: relative;

    &:first-child {
      border-top-left-radius: 16px;
    }

    &:last-child {
      border-top-right-radius: 16px;
    }
  }

  .ant-table-tbody > tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f5f5f5;

    &:hover > td {
      background: #fafbff !important;
      transform: translateY(-1px);
    }

    &:last-child {
      border-bottom: none;

      td {
        border-bottom: none;
      }
    }

    &:nth-child(even) {
      background: #fafafa;

      &:hover > td {
        background: #f0f7ff !important;
      }
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: none;
    padding: 18px 20px;
    font-size: 14px;
    line-height: 1.5;
    vertical-align: middle;
    text-align: left;
    transition: all 0.2s ease;
  }

  /* Hide scrollbar on header */
  .ant-table-header {
    overflow: hidden !important;

    &::-webkit-scrollbar {
      display: none;
      height: 0;
    }

    scrollbar-width: none;
  }

  /* Scrollbar styling - body rows only */
  .ant-table-body {
    overflow: auto !important;

    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f5f5f5;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;
      border: 1px solid #f5f5f5;

      &:hover {
        background: #a8a8a8;
      }
    }

    &::-webkit-scrollbar-corner {
      background: #f5f5f5;
    }

    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 #f5f5f5;
  }

  /* Fixed column styling */
  .ant-table-cell-fix-right {
    background: white !important;
    box-shadow: -6px 0 16px -8px rgba(0, 0, 0, 0.08);
  }

  .ant-table-thead > tr > th.ant-table-cell-fix-right {
    background: white !important;
  }

  .ant-table-tbody > tr:nth-child(even) > td.ant-table-cell-fix-right {
    background: #fafafa !important;
  }

  .ant-table-tbody > tr:hover > td.ant-table-cell-fix-right {
    background: #fafbff !important;
  }

  .ant-table-tbody > tr:nth-child(even):hover > td.ant-table-cell-fix-right {
    background: #f0f7ff !important;
  }

  /* Pagination styling */
  .ant-pagination {
    margin: 0 !important;
    padding: 12px 16px !important;
    border-top: 1px solid ${COLORS.border};
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    .ant-pagination-total-text {
      font-size: 13px;
      color: ${COLORS.textMedium};
      font-weight: 500;
      flex: 1;
    }

    .ant-pagination-options {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 32px;

      .ant-pagination-options-size-changer {
        height: 32px !important;

        .ant-select-selector {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          min-width: 100px !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 28px 0 12px !important;
          box-sizing: border-box !important;

          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
        }

        .ant-select-selection-item {
          height: 28px !important;
          line-height: 28px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          font-weight: 500 !important;
          font-size: 13px !important;
          flex: 1 !important;
          min-width: 0 !important;
        }

        .ant-select-selection-search {
          height: 28px !important;
          padding-left: 8px !important;

          .ant-select-selection-search-input {
            height: 28px !important;
            line-height: 28px !important;
          }
        }

        .ant-select-arrow {
          right: 10px !important;
          flex-shrink: 0 !important;
          height: 14px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;

          .anticon {
            line-height: 1 !important;
            vertical-align: middle !important;
          }
        }

        &.ant-select-focused .ant-select-selector {
          border-color: ${COLORS.$primaryBlue} !important;
          box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
        }
      }

      .ant-pagination-options-quick-jumper {
        display: flex;
        align-items: center;
        height: 32px;
        font-size: 13px;

        .ant-input {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          width: 50px !important;
          text-align: center;
          font-weight: 600;
          font-size: 13px;
          transition: all 0.3s ease !important;
          line-height: 28px !important;
          padding: 0 0 0 8px !important;

          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }

          &:focus {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
          }
        }
      }
    }

    .ant-pagination-item {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      a {
        color: ${COLORS.textDark};
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        text-decoration: none;
        font-size: 13px;
      }

      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);

        a {
          color: ${COLORS.$primaryBlue};
        }
      }

      &.ant-pagination-item-active {
        background: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        transform: translateY(-1px);

        a {
          color: white;
          font-weight: 700;
        }

        &:hover {
          background: ${COLORS.$primaryBlueDark};
          border-color: ${COLORS.$primaryBlueDark};

          a {
            color: white;
          }
        }
      }
    }

    .ant-pagination-prev,
    .ant-pagination-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      .anticon {
        color: ${COLORS.textDark};
        font-size: 14px;
      }

      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);

        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }

      &.ant-pagination-disabled {
        border-color: #e8e8e8;
        background: #f5f5f5;

        .anticon {
          color: ${COLORS.textMuted};
        }

        &:hover {
          border-color: #e8e8e8;
          background: #f5f5f5;
          transform: none;

          .anticon {
            color: ${COLORS.textMuted};
          }
        }
      }
    }

    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      .anticon {
        color: ${COLORS.textMuted};
        font-size: 14px;
      }

      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);

        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
    }
  }
`;

export const StatusTag = styled(Tag)`
  border-radius: 6px;
  font-weight: 500;
  font-size: 12px;
  padding: 2px 10px;
  border: none;
`;

export const EventTypeTag = styled(Tag)`
  border-radius: 4px;
  font-size: 12px;
  margin: 2px;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  color: #555;
`;

export const StaffingBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;

  .staffing-text {
    font-size: 13px;
    font-weight: 500;
    color: ${props => {
      if (props.$status === 'fully_staffed') return '#52c41a';
      if (props.$status === 'partially_staffed') return '#faad14';
      return '#ff4d4f';
    }};
    white-space: nowrap;
  }

  .ant-progress {
    flex: 1;
    min-width: 60px;
  }
`;

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-modal-header {
    padding: 20px 24px;
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-modal-title {
    font-size: 18px;
    font-weight: 600;
  }

  .ant-modal-body {
    padding: 24px;
    max-height: 65vh;
    overflow-y: auto;
  }

  .ant-modal-footer {
    padding: 16px 24px;
    border-top: 1px solid #f0f0f0;
  }
`;

export const FormSection = styled.div`
  margin-bottom: 28px;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: ${COLORS.$charcoalBlack};
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  
  .icon {
    font-size: 48px;
    color: #d9d9d9;
    margin-bottom: 16px;
  }
  
  .title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }
  
  .description {
    font-size: 14px;
    color: #888;
    margin-bottom: 24px;
  }

  button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 20px;
    border-radius: 8px;
    font-weight: 500;
    background: #1890ff !important;
    border-color: #1890ff !important;
    color: #fff !important;
    transition: all 0.2s ease;

    &:hover {
      background: #40a9ff !important;
      border-color: #40a9ff !important;
      color: #fff !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
    }
  }
`;

export const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

export const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  flex: 1;
  min-width: 150px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 16px;

  .stat-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    background: ${props => props.$bgColor || '#f0f0f0'};
    color: ${props => props.$iconColor || '#333'};
  }

  .stat-info {
    .stat-value {
      font-size: 24px;
      font-weight: 700;
      color: ${COLORS.$charcoalBlack};
      line-height: 1;
    }

    .stat-label {
      font-size: 13px;
      color: #888;
      margin-top: 4px;
    }
  }
`;

export const CompactButton = styled(Button)`
  &&& {
    height: ${props => props.$h || 24}px;
    min-height: ${props => props.$h || 24}px !important;
    max-height: ${props => props.$h || 24}px !important;
    padding: 0 ${props => props.$px ?? 12}px;
    font-size: ${props => props.$fs || 12}px;
    font-weight: 500;
    line-height: 1;
    border-radius: ${props => props.$radius || 5}px;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    ${props => props.$danger && `
      color: #cf1322 !important;
      border-color: #ffa39e !important;
      background: #fff !important;

      &:hover {
        color: #ff4d4f !important;
        border-color: #ff4d4f !important;
        background: #fff1f0 !important;
      }
    `}
  }
`;

export const AssignmentList = styled.div`
  .assignment-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    margin-bottom: 12px;
    background: #fafafa;
    .hcw-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .hcw-name {
        font-weight: 500;
        font-size: 14px;
      }

      .hcw-contact {
        font-size: 12px;
        color: #888;
      }
    }

    .assignment-actions {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .no-assignments {
    text-align: center;
    padding: 20px;
    color: #aaa;
    font-size: 13px;
  }
`;

export const StatusHistoryList = styled.div`
  .history-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
      border-bottom: none;
    }

    .history-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #1890ff;
      margin-top: 6px;
      flex-shrink: 0;
    }

    .history-content {
      flex: 1;

      .history-status {
        font-weight: 500;
        font-size: 14px;
        text-transform: capitalize;
      }

      .history-meta {
        font-size: 12px;
        color: #888;
        margin-top: 2px;
      }

      .history-reason {
        font-size: 12px;
        color: #666;
        margin-top: 4px;
        font-style: italic;
      }
    }
  }
`;
